import React, { useState, useCallback } from 'react';

import Modal from '../Modal/Modal';

export type BetaWarningModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const BETA_WARNING_MODAL_KEY = 'acconto-beta-warning-seen';

const BetaWarningModal: React.FC<BetaWarningModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [hideNextTime, setHideNextTime] = useState(false);

  const handleClose = useCallback(() => {
    if (hideNextTime && typeof window !== 'undefined') {
      localStorage.setItem(BETA_WARNING_MODAL_KEY, 'true');
    }
    onClose();
  }, [hideNextTime, onClose]);

  const footer = (
    <div className='px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-lg'>
      <div className='flex items-center justify-between mb-3'>
        <label className='flex items-center cursor-pointer'>
          <input
            type='checkbox'
            checked={hideNextTime}
            onChange={e => setHideNextTime(e.target.checked)}
            className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600'
          />
          <span className='ml-2 text-sm text-gray-600 dark:text-gray-400 italic'>
            diese Nachricht nicht mehr anzeigen
          </span>
        </label>
        <button
          onClick={handleClose}
          className='px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium'
        >
          Verstanden
        </button>
      </div>
      <div className='text-center italic text-xs text-gray-500 dark:text-gray-400'>
        © 2025 Acconto.ai – Beta Version 0.9.2
      </div>
    </div>
  );

  const title = (
    <span className='text-2xl font-bold text-blue-600 dark:text-blue-500'>
      Willkommen in der a
      <span className='text-gray-900 dark:text-gray-50'>cconto</span>.
      <span className='text-gray-900 dark:text-gray-50'>ai</span> Beta!
    </span>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onClose={handleClose}
      size='xl'
      footer={footer}
      showCloseButton={false}
      titleAlign='center'
      disableInitialFocus={true}
    >
      <div className='text-gray-700 dark:text-gray-300 space-y-4 text-center'>
        {/* Greeting */}
        <p className='text-lg'>
          Hey 👋
          <br />
          schön, dass du Acconto.ai in der{' '}
          <span className='font-semibold'>Beta-Phase</span> testest –<br />
          und damit aktiv hilfst, die Zukunft der Buchhaltung mitzugestalten.
        </p>

        {/* Important Notice Section */}
        <div className='mt-6'>
          <p className='font-semibold text-gray-900 dark:text-gray-100'>
            Bitte beachte:
          </p>
          <p className='mt-2'>
            Diese Version befindet sich noch in Entwicklung.
            <br />
            Es kann daher vereinzelt zu{' '}
            <span className='font-semibold'>
              Fehlern, Unstimmigkeiten
            </span>{' '}
            oder{' '}
            <span className='font-semibold'>abweichenden Ergebnissen</span>{' '}
            kommen.
            <br />
            Wir übernehmen in dieser Testphase{' '}
            <span className='font-semibold'>
              keine Haftung für fehlerhafte Buchungen oder daraus resultierende
              Schäden
            </span>
            .
          </p>
        </div>

        {/* Important Reminder Section */}
        <div className='mt-6'>
          <p className='font-semibold text-gray-900 dark:text-gray-100'>
            Wichtig:
          </p>
          <p className='mt-2'>
            Bitte{' '}
            <span className='font-semibold'>
              prüfe deine ersten Buchungen sorgfältig
            </span>
            , bevor du sie weiterverarbeitest oder exportierst (z. B. nach DATEV
            oder Lexoffice).
            <br />
            So stellst du sicher, dass alles korrekt zugeordnet ist und wir
            eventuelle Unstimmigkeiten früh erkennen können.
          </p>
        </div>

        {/* Feedback Section */}
        <div className='mt-6'>
          <p>
            Wenn dir etwas auffällt, freuen wir uns über dein Feedback!
            <br />
            Sende einfach eine kurze Beschreibung + Screenshot an
            <br />
            <a
              href='mailto:help@acconto.helpscoutapp.com'
              className='text-blue-600 dark:text-blue-400 hover:underline'
            >
              help@acconto.helpscoutapp.com
            </a>
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default BetaWarningModal;
